<script setup>
import { ref, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { channels, config, number } from './config';
import SectionHeading from './components/SectionHeading.vue';
import PricingSection from './components/PricingSection.vue';
import ActivationForm from './components/ActivationForm.vue';

const { t, tm } = useI18n();
const assets = {
  logo: '/brand-assets/chatiom-logo.svg',
  icon: '/brand-assets/chatiom-icon.svg',
  inbox: '/landing/conversation-light.jpg',
  inboxDark: '/landing/conversation-dark.jpg',
};
const menuOpen = ref(false);
const selection = ref({
  plan: 'free',
  users: 2,
  accounts: 1,
  messages: 0,
  storage: 0.5,
});
const navItems = ['features', 'channels', 'pricing', 'faq'];
const features = [
  { id: 'assignment', icon: 'i-fluent-person-arrow-right-24-regular' },
  { id: 'notes', icon: 'i-fluent-note-24-regular' },
  { id: 'replies', icon: 'i-fluent-chat-multiple-24-regular' },
  { id: 'reports', icon: 'i-fluent-data-bar-vertical-24-regular' },
];
async function selectPlan(
  key = 'free',
  capacity = { users: 2, accounts: 1, messages: 0, storage: 0.5 }
) {
  selection.value = { plan: key, ...capacity };
  menuOpen.value = false;
  await nextTick();
  document.getElementById('request').scrollIntoView();
  document
    .querySelector('#request input[name="name"]')
    .focus({ preventScroll: true });
}
</script>

<template>
  <div dir="rtl" class="overflow-x-clip">
    <header
      class="sticky top-0 z-30 border-b border-n-slate-3 bg-white/95 backdrop-blur"
    >
      <div
        class="mx-auto flex h-20 max-w-7xl items-center justify-between gap-5 px-5 md:h-24 md:px-8"
      >
        <a
          href="#"
          class="shrink-0 rounded focus-visible:ring-2 focus-visible:ring-woot-600"
          ><img
            :src="assets.logo"
            alt="Chatiom"
            width="170"
            height="44"
            class="h-9 w-auto md:h-10"
        /></a>
        <nav class="hidden items-center gap-7 text-sm text-n-slate-8 lg:flex">
          <a
            v-for="item in navItems"
            :key="item"
            :href="`#${item}`"
            class="rounded transition-colors hover:text-woot-600 focus-visible:ring-2 focus-visible:ring-woot-600"
            >{{ t(`nav.${item}`) }}</a
          >
        </nav>
        <div class="flex items-center gap-5">
          <a
            href="/app/login"
            class="hidden text-sm text-n-slate-11 sm:block"
            >{{ t('nav.login') }}</a
          ><button
            class="hidden rounded-xl bg-woot-600 px-5 py-3 text-sm font-bold text-white hover:bg-woot-700 focus-visible:ring-2 focus-visible:ring-woot-600 focus-visible:ring-offset-4 sm:block"
            @click="selectPlan()"
          >
            {{ t('activate') }}
            <span class="ms-3">{{ t('arrow') }}</span></button
          ><button
            :aria-label="t('nav.menu')"
            :aria-expanded="menuOpen"
            aria-controls="mobile-menu"
            class="flex h-11 w-11 items-center justify-center rounded-xl border border-n-slate-4 focus-visible:ring-2 focus-visible:ring-woot-600 lg:hidden"
            @click="menuOpen = !menuOpen"
          >
            <span
              class="i-fluent-navigation-24-regular h-6 w-6"
              aria-hidden="true"
            />
          </button>
        </div>
      </div>
      <nav
        v-if="menuOpen"
        id="mobile-menu"
        class="space-y-1 border-t border-n-slate-3 px-5 py-4 lg:hidden"
      >
        <a
          v-for="item in navItems"
          :key="item"
          :href="`#${item}`"
          class="block rounded-lg p-3 text-sm hover:bg-n-slate-2"
          @click="menuOpen = false"
          >{{ t(`nav.${item}`) }}</a
        ><a href="/app/login" class="block p-3 text-sm">{{ t('nav.login') }}</a
        ><button
          class="w-full rounded-xl bg-woot-600 p-3 text-sm text-white"
          @click="selectPlan()"
        >
          {{ t('activate') }}
        </button>
      </nav>
    </header>

    <main>
      <section
        class="relative mx-auto max-w-7xl px-5 pb-16 pt-12 md:px-8 md:pb-24 md:pt-20"
      >
        <div class="grid items-center gap-12 lg:grid-cols-2 lg:gap-8">
          <div>
            <p
              class="mb-6 inline-flex items-center gap-2 text-sm text-n-slate-8"
            >
              <span class="h-2 w-2 rounded-full bg-n-accent" />{{
                t('hero.eyebrow')
              }}
            </p>
            <h1
              class="text-4xl font-bold leading-[1.65] tracking-tight sm:text-5xl sm:leading-[1.6] lg:text-[3.5rem] lg:leading-[1.55]"
            >
              {{ t('hero.first') }}<br /><span class="text-woot-600">{{
                t('hero.highlight')
              }}</span
              ><br />{{ t('hero.last') }}
            </h1>
            <p class="mt-6 max-w-lg text-base leading-9 text-n-slate-8">
              {{ t('hero.description') }}
            </p>
            <div class="mt-8 flex flex-col gap-3 sm:flex-row">
              <button
                class="rounded-xl bg-woot-600 px-6 py-4 text-sm font-bold text-white shadow-lg shadow-n-blue-9/10 transition-colors hover:bg-woot-700 focus-visible:ring-2 focus-visible:ring-woot-600 focus-visible:ring-offset-4"
                @click="selectPlan()"
              >
                {{ t('freeStart') }}
                <span class="ms-3">{{ t('arrow') }}</span></button
              ><a
                href="#product"
                class="flex items-center justify-center gap-2 rounded-xl border border-n-slate-4 px-5 py-4 text-sm font-medium text-n-slate-11 hover:bg-n-slate-2"
                ><span
                  class="i-fluent-play-circle-24-regular h-5 w-5"
                  aria-hidden="true"
                />{{ t('hero.demo') }}</a
              >
            </div>
            <p class="mt-5 text-xs text-n-slate-7">{{ t('hero.note') }}</p>
          </div>
          <figure id="product" class="min-w-0 scroll-mt-28">
            <div
              class="rounded-2xl border border-n-blue-3 bg-gradient-to-bl from-n-blue-2 via-n-slate-2 to-n-accent/10 p-3 shadow-2xl shadow-n-blue-12/10 md:p-4"
            >
              <div
                class="mb-3 flex items-center justify-between px-1 text-xs text-n-slate-8"
              >
                <span class="flex items-center gap-2"
                  ><img :src="assets.icon" alt="" class="h-5 w-5" />{{
                    t('hero.status')
                  }}</span
                ><span aria-hidden="true"
                  ><span class="h-2 w-2 rounded-full bg-n-slate-5" /><span
                    class="h-2 w-2 rounded-full bg-n-slate-5" /><span
                    class="h-2 w-2 rounded-full bg-n-accent"
                /></span>
              </div>
              <img
                :src="assets.inbox"
                :alt="t('hero.imageAlt')"
                width="1280"
                height="720"
                fetchpriority="high"
                class="w-full rounded-lg border border-n-slate-4 bg-white"
              />
            </div>
            <figcaption
              class="mt-5 text-center text-xs leading-6 text-n-slate-7"
            >
              {{ t('hero.caption') }}
            </figcaption>
          </figure>
        </div>
      </section>

      <section
        id="channels"
        class="scroll-mt-24 border-y border-n-slate-3 bg-n-slate-2 py-12 md:py-16"
      >
        <div class="mx-auto max-w-7xl px-5 md:px-8">
          <SectionHeading
            :title="t('channels.title')"
            :description="t('channels.description')"
          />
          <div class="grid grid-cols-4 gap-3 md:grid-cols-8 md:gap-4">
            <div
              v-for="channel in channels"
              :key="channel.id"
              class="flex min-h-32 flex-col items-center justify-center rounded-2xl border border-n-slate-4 bg-white px-2 py-5"
            >
              <img
                v-if="channel.image"
                :src="channel.image"
                :alt="t(`channels.${channel.id}`)"
                width="40"
                height="40"
                loading="lazy"
                class="mb-4 h-10 w-10 object-contain"
              />
              <p class="text-xs sm:text-sm">
                {{ t(`channels.${channel.id}`) }}
              </p>
              <span class="mt-2 min-h-4 text-[0.625rem] text-n-slate-7">{{
                channel.id === 'instagram'
                  ? t('channels.soon')
                  : channel.id === 'whatsapp'
                    ? t('channels.coordinated')
                    : ''
              }}</span>
            </div>
          </div>
        </div>
      </section>

      <section id="features" class="scroll-mt-24 py-20 md:py-28">
        <div class="mx-auto max-w-7xl px-5 md:px-8">
          <SectionHeading
            :eyebrow="t('features.eyebrow')"
            :title="t('features.title')"
            :description="t('features.description')"
          />
          <div class="grid gap-5 lg:grid-cols-2">
            <article
              class="overflow-hidden rounded-3xl border border-n-blue-3 bg-n-blue-2 p-6 md:p-8"
            >
              <span
                class="i-fluent-chat-multiple-24-regular mb-5 block h-8 w-8 text-woot-600"
                aria-hidden="true"
              />
              <h3 class="text-xl font-bold">{{ t('features.inbox.title') }}</h3>
              <p class="mb-8 mt-3 text-sm leading-8 text-n-slate-8">
                {{ t('features.inbox.description') }}
              </p>
              <img
                :src="assets.inboxDark"
                :alt="t('hero.imageAlt')"
                width="1280"
                height="720"
                loading="lazy"
                class="w-full rounded-xl border border-n-slate-4 shadow-xl"
              />
              <p class="mt-3 text-xs text-n-slate-8">{{ t('hero.caption') }}</p>
            </article>
            <div class="grid gap-5 sm:grid-cols-2">
              <article
                v-for="feature in features"
                :key="feature.id"
                class="rounded-3xl border border-n-slate-4 bg-white p-6 md:p-7"
              >
                <span
                  class="mb-7 flex h-12 w-12 items-center justify-center rounded-2xl bg-n-accent/10 text-n-accent"
                  ><span
                    :class="feature.icon"
                    class="h-6 w-6"
                    aria-hidden="true"
                /></span>
                <h3 class="text-lg font-bold leading-8">
                  {{ t(`features.${feature.id}.title`) }}
                </h3>
                <p class="mt-3 text-sm leading-8 text-n-slate-8">
                  {{ t(`features.${feature.id}.description`) }}
                </p>
              </article>
            </div>
          </div>
        </div>
      </section>

      <section class="border-y border-n-slate-3 bg-n-slate-2 py-16 md:py-20">
        <div class="mx-auto max-w-7xl px-5 md:px-8">
          <SectionHeading
            :eyebrow="t('steps.eyebrow')"
            :title="t('steps.title')"
            :description="t('steps.description')"
          />
          <ol class="grid gap-8 md:grid-cols-3">
            <li
              v-for="(step, index) in tm('steps.items')"
              :key="step.title"
              class="border-t border-n-slate-4 pt-6"
            >
              <span
                class="mb-5 flex h-11 w-11 items-center justify-center rounded-full bg-white text-lg font-bold text-woot-600 ring-1 ring-n-slate-4"
                >{{ number(index + 1) }}</span
              >
              <h3 class="text-lg font-bold">{{ step.title }}</h3>
              <p class="mt-3 max-w-sm text-sm leading-8 text-n-slate-8">
                {{ step.description }}
              </p>
            </li>
          </ol>
        </div>
      </section>
      <section class="py-20">
        <div class="mx-auto max-w-7xl px-5 md:px-8">
          <SectionHeading :title="t('uses.title')" />
          <div class="grid gap-5 md:grid-cols-3">
            <article
              v-for="(item, index) in tm('uses.items')"
              :key="item.title"
              class="rounded-2xl border border-n-slate-4 p-7"
            >
              <span
                :class="
                  [
                    'i-fluent-building-shop-24-regular',
                    'i-fluent-people-team-24-regular',
                    'i-fluent-building-24-regular',
                  ][index]
                "
                class="mb-5 block h-7 w-7 text-woot-600"
                aria-hidden="true"
              />
              <h3 class="text-xl font-bold">{{ item.title }}</h3>
              <p class="mt-3 text-sm leading-8 text-n-slate-8">
                {{ item.description }}
              </p>
            </article>
          </div>
        </div>
      </section>

      <section class="mx-auto max-w-7xl px-5 md:px-8">
        <div
          class="relative overflow-hidden rounded-3xl bg-n-slate-12 p-8 text-white md:p-14"
        >
          <div
            class="absolute -end-20 -top-20 h-80 w-80 rounded-full bg-n-blue-9/20 blur-3xl"
            aria-hidden="true"
          />
          <div class="relative grid items-center gap-8 md:grid-cols-2">
            <div>
              <span
                class="inline-block rounded-full border border-n-accent/30 bg-n-accent/10 px-4 py-2 text-xs text-n-accent"
                >{{ t('ai.badge') }}</span
              >
              <h2
                class="mb-4 mt-6 text-2xl font-bold leading-relaxed md:text-3xl"
              >
                {{ t('ai.title') }}
              </h2>
              <p class="max-w-lg text-sm leading-8 text-n-slate-5">
                {{ t('ai.description') }}
              </p>
              <p class="mt-5 text-xs leading-7 text-n-slate-7">
                {{ t('ai.note') }}
              </p>
            </div>
            <div class="space-y-3">
              <div
                v-for="item in tm('ai.items')"
                :key="item"
                class="flex items-center gap-4 rounded-xl border border-white/10 bg-white/5 p-5"
              >
                <span
                  class="i-fluent-sparkle-24-regular h-5 w-5 text-n-accent"
                  aria-hidden="true"
                /><span class="text-sm">{{ item }}</span
                ><span class="ms-auto text-xs text-n-slate-7">{{
                  t('channels.soon')
                }}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <PricingSection @select="selectPlan" />
      <section
        id="faq"
        class="scroll-mt-24 border-y border-n-slate-3 bg-n-slate-2 py-16 md:py-20"
      >
        <div class="mx-auto grid max-w-7xl gap-8 px-5 md:grid-cols-3 md:px-8">
          <SectionHeading :eyebrow="t('faq.eyebrow')" :title="t('faq.title')" />
          <div class="space-y-3 md:col-span-2">
            <details
              v-for="item in tm('faq.items')"
              :key="item.question"
              class="group rounded-xl border border-n-slate-4 bg-white"
            >
              <summary
                class="flex cursor-pointer list-none items-center justify-between gap-4 rounded-xl p-5 text-sm font-bold focus-visible:ring-2 focus-visible:ring-woot-600"
              >
                {{ item.question
                }}<span
                  class="i-fluent-chevron-down-24-regular h-5 w-5 shrink-0 text-n-slate-7 group-open:rotate-180"
                  aria-hidden="true"
                />
              </summary>
              <p class="px-5 pb-5 text-sm leading-8 text-n-slate-8">
                {{ item.answer }}
              </p>
            </details>
          </div>
        </div>
      </section>

      <section
        id="request"
        class="scroll-mt-28 bg-gradient-to-bl from-n-blue-2/70 via-white to-n-accent/10 py-20 md:py-28"
      >
        <div
          class="mx-auto grid max-w-7xl items-start gap-10 px-5 md:grid-cols-2 md:px-8 lg:gap-20"
        >
          <div class="md:pt-8">
            <SectionHeading
              :eyebrow="t('form.eyebrow')"
              :title="t('form.title')"
              :description="t('form.description')"
            />
            <div class="mt-8 flex items-center gap-3 text-sm text-n-slate-8">
              <span
                class="i-fluent-handshake-24-regular h-7 w-7 text-n-accent"
                aria-hidden="true"
              />{{ t('hero.note') }}
            </div>
          </div>
          <ActivationForm :selection="selection" />
        </div>
      </section>
    </main>
    <footer class="border-t border-n-slate-4 py-10">
      <div class="mx-auto max-w-7xl px-5 md:px-8">
        <div
          class="flex flex-col justify-between gap-8 md:flex-row md:items-center"
        >
          <div>
            <a href="#"
              ><img
                :src="assets.logo"
                alt="Chatiom"
                width="150"
                height="40"
                class="h-9 w-auto"
            /></a>
            <p class="mt-4 text-xs leading-7 text-n-slate-8">
              {{ t('footer.tagline') }}
            </p>
          </div>
          <nav class="flex flex-wrap gap-6 text-sm text-n-slate-8">
            <a
              v-for="item in navItems"
              :key="item"
              :href="`#${item}`"
              class="hover:text-woot-600"
              >{{ t(`nav.${item}`) }}</a
            >
          </nav>
        </div>
        <div
          class="mt-8 flex flex-wrap justify-between gap-4 border-t border-n-slate-3 pt-6 text-xs text-n-slate-7"
        >
          <p>{{ t('footer.copyright') }}</p>
          <div class="flex flex-wrap gap-4">
            <a
              v-if="config.contact_email"
              :href="`mailto:${config.contact_email}`"
              dir="ltr"
              >{{ config.contact_email }}</a
            ><a
              v-if="config.contact_phone"
              :href="`tel:${config.contact_phone}`"
              dir="ltr"
              >{{ config.contact_phone }}</a
            ><span>{{ t('footer.infrastructure') }}</span>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>
